<!-- Componente completo corregido -->
<template>
  <v-container>
    <h1>Comprobante de Informe Diario Facturación Electrónica</h1>
    <v-row class="mt-6">
      <v-col cols="6" sm="3" md="3">
        <v-text-field v-model="issueDate" label="Fecha" outlined type="date" dense />
      </v-col>
      <v-col cols="6" sm="3" md="3">
        <v-btn @click="get_docs" color="primary">Buscar</v-btn>
      </v-col>
      <v-col cols="6" sm="3" md="3">
        <v-btn text @click="generatePDF" color="info">Print</v-btn>
      </v-col>
    </v-row>
    <v-row class="mt-6" ref="printArea">
      <!-- Sección de información del vendedor -->
      <v-col cols="12" md="12" v-if="seller.PartyIdentification">
        <v-data-table
          mobile-breakpoint="0"
          :headers="header_store"
          :items="[seller]"
          class="elevation-4"
          :loading="loading_status"
          loader-height="1"
          loading-text="Cargando ..."
          hide-default-footer
          dense
        />
      </v-col>

      <!-- Sección de resoluciones -->
      <v-col cols="12" md="12">
        <v-card flat>
          <v-card-title>
            Resolución de Facturación Electrónica
            <v-spacer></v-spacer>
          </v-card-title>
          <v-data-table
            mobile-breakpoint="0"
            :headers="header_resolution"
            :items="resolutions"
            class="elevation-4"
            :loading="loading_status"
            loader-height="1"
            loading-text="Cargando ..."
            hide-default-footer
            dense
          />
        </v-card>
      </v-col>

      <!-- Sección de pagos -->
      <v-col cols="12">
        <v-card flat>
          <v-card-title> Recaudos de Venta </v-card-title>
          <v-data-table
            mobile-breakpoint="0"
            :headers="header_payments"
            :items="payments"
            :items-per-page="-1"
            class="elevation-4"
            item-key="id_pos,ean"
            :loading="loading_status"
            loader-height="1"
            loading-text="Cargando ..."
            hide-default-footer
            dense
          >
            <template v-slot:[`item.quantity`]="{ item }">
              {{ parseFloat(item.quantity).toLocaleString("es") }}
            </template>
            <template v-slot:[`item.value`]="{ item }">
              {{ formatCurrency(parseFloat(item.value || 0)) }}
            </template>

            <template slot="body.append">
              <tr>
                <td>Total</td>
                <td class="text-right">{{ getTotalFormatted(payments, "quantity") }}</td>
                <td class="text-right">
                  {{ formatCurrency(getTotal(payments, "value")) }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <!-- Sección de categorías con tasa explícita -->
      <v-col cols="12">
        <v-card flat>
          <v-card-title> Discriminación de Ventas por Departamento </v-card-title>
          <v-data-table
            mobile-breakpoint="0"
            :headers="header_sales"
            :items="sales"
            :items-per-page="-1"
            class="elevation-4"
            item-key="category,tax_rate"
            :loading="loading_status"
            loader-height="1"
            loading-text="Cargando ..."
            hide-default-footer
            dense
          >
            <template #top> </template>

            <template v-slot:[`item.tax_rate`]="{ item }">
              {{ item.tax_rate }}%
            </template>
            <template v-slot:[`item.quantity`]="{ item }">
              {{ parseFloat(item.quantity).toLocaleString("es") }}
            </template>
            <template v-slot:[`item.taxes`]="{ item }">
              {{ formatCurrency(parseFloat(item.taxes || 0)) }}
            </template>
            <template v-slot:[`item.price`]="{ item }">
              {{ formatCurrency(parseFloat(item.price || 0)) }}
            </template>
            <template v-slot:[`item.tax_amount`]="{ item }">
              {{ calculateTaxAmount(item.price, item.tax_rate) }}
            </template>
            <template v-slot:[`item.total`]="{ item }">
              {{
                formatCurrency(parseFloat(item.price || 0) + parseFloat(item.taxes || 0))
              }}
            </template>

            <template slot="body.append">
              <tr>
                <td>Total</td>

                <td class="text-right">{{ getTotalFormatted(sales, "quantity") }}</td>
                <td class="text-right">{{ formatCurrency(getTotal(sales, "taxes")) }}</td>
                <td class="text-right">-</td>
                <td class="text-right">{{ formatCurrency(getTotal(sales, "price")) }}</td>

                <td class="text-right">
                  {{
                    formatCurrency(getTotal(sales, "price") + getTotal(sales, "taxes"))
                  }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <!-- Sección de facturas con tasa explícita -->
      <v-col cols="12">
        <v-card flat>
          <v-card-title> Registros de Facturas por Tasa de Impuesto </v-card-title>
          <v-data-table
            mobile-breakpoint="0"
            :headers="header_invoices"
            :items="invoices"
            :items-per-page="-1"
            class="elevation-4"
            item-key="invoice,tax_rate"
            :loading="loading_status"
            loader-height="1"
            loading-text="Cargando ..."
            hide-default-footer
            dense
          >
            <template v-slot:[`item.tax_rate`]="{ item }">
              {{ item.tax_rate }}%
            </template>
            <template v-slot:[`item.subtotal`]="{ item }">
              {{ formatCurrency(parseFloat(item.subtotal || 0)) }}
            </template>
            <template v-slot:[`item.discount`]="{ item }">
              {{ formatCurrency(parseFloat(item.discount || 0)) }}
            </template>
            <template v-slot:[`item.taxes`]="{ item }">
              {{ formatCurrency(parseFloat(item.taxes || 0)) }}
            </template>
            <template v-slot:[`item.price`]="{ item }">
              {{ formatCurrency(parseFloat(item.price || 0)) }}
            </template>
            <template v-slot:[`item.tax_amount`]="{ item }">
              {{ calculateTaxAmount(item.price, item.tax_rate) }}
            </template>
            <template v-slot:[`item.total`]="{ item }">
              {{ formatCurrency(parseFloat(item.total || 0)) }}
            </template>
            <template v-slot:[`item.bags`]="{ item }">
              <!-- Mostrar el impuesto de bolsas solo en la primera tasa de cada factura -->
              {{ shouldShowBags(item) ? formatCurrency(parseFloat(item.bags || 0)) : "" }}
            </template>
            <template v-slot:[`item.quantity`]="{ item }">
              {{ parseFloat(item.quantity).toLocaleString("es") }}
            </template>

            <template slot="body.append">
              <tr>
                <td>Total</td>

                <td class="text-right">
                  {{ formatCurrency(getTotal(invoices, "subtotal")) }}
                </td>
                <td class="text-right">
                  {{ formatCurrency(getTotal(invoices, "discount")) }}
                </td>
                <td class="text-right">
                  {{ formatCurrency(getTotal(invoices, "price")) }}
                </td>
                <td class="text-right">
                  {{ formatCurrency(getTotal(invoices, "taxes")) }}
                </td>
                <td class="text-right">-</td>
                <td class="text-right">
                  {{ formatCurrency(getTotal(invoices, "total")) }}
                </td>
                <td class="text-right">
                  {{ formatCurrency(getTotalBags()) }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { webserver, getToday } from "../services/webserver.js";
import jsPDF from "jspdf";
import "jspdf-autotable";
export default {
  name: "ReportDay",
  data() {
    return {
      loading_status: false,
      stores: [],
      store: "",
      issueDate: getToday(),
      payments: [],
      sales: [],
      invoices: [],
      // Para rastrear qué facturas ya han mostrado el impuesto de bolsas
      processedInvoices: new Set(),
      resolutions: [],
      seller: {
        AddressLine: "",
        CityName: "",
        PartyIdentification: "",
        PartyIdentification_Id: "",
        PartyName: "",
        TaxLevelCode: "",
        client_email: "",
        client_phone: "",
        store_Caja: "",
        store_id: "",
        store_name: "",
      },
      header_store: [
        {
          text: "Nombre",
          align: "start",
          sortable: true,
          filterable: true,
          value: "PartyName",
          dataType: "text",
        },
        {
          text: "Identificación",
          align: "center",
          sortable: true,
          filterable: true,
          value: "PartyIdentification",
          dataType: "text",
        },
        {
          text: "Dirección",
          align: "center",
          sortable: true,
          filterable: true,
          value: "AddressLine",
          dataType: "text",
        },
        {
          text: "Ciudad",
          align: "center",
          sortable: true,
          filterable: true,
          value: "CityName",
          dataType: "text",
        },
        {
          text: "Email",
          align: "center",
          sortable: true,
          filterable: true,
          value: "client_email",
          dataType: "text",
        },
        {
          text: "Telefono",
          align: "center",
          sortable: true,
          filterable: true,
          value: "client_phone",
          dataType: "text",
        },
      ],
      header_resolution: [
        {
          text: "Autorización",
          align: "start",
          sortable: true,
          filterable: true,
          value: "InvoiceAuthorization",
          dataType: "text",
        },
        {
          text: "Prefijo",
          align: "center",
          sortable: true,
          filterable: true,
          value: "Prefix",
          dataType: "number",
        },
        {
          text: "Desde",
          align: "center",
          sortable: true,
          filterable: true,
          value: "AuthorizationFrom",
          dataType: "number",
        },
        {
          text: "Hasta",
          align: "center",
          sortable: true,
          filterable: true,
          value: "AuthorizationTo",
          dataType: "number",
        },
        {
          text: "Desde",
          align: "center",
          sortable: true,
          filterable: true,
          value: "StartDate",
        },
        {
          text: "Hasta",
          align: "center",
          sortable: true,
          filterable: true,
          value: "EndDate",
        },
      ],
      // Cabeceras actualizadas para incluir la tasa aplicada y total
      header_sales: [
        {
          text: "Categoría",
          align: "start",
          sortable: true,
          filterable: true,
          value: "category",
          dataType: "text",
        },

        {
          text: "Cantidad",
          align: "end",
          sortable: true,
          filterable: true,
          value: "quantity",
          dataType: "number",
        },
        {
          text: "Impuesto",
          align: "end",
          sortable: true,
          filterable: true,
          value: "taxes",
          dataType: "number",
        },
        {
          text: "% ",
          align: "center",
          sortable: true,
          filterable: true,
          value: "tax_rate",
          dataType: "number",
        },
        {
          text: "Valor Base",
          align: "end",
          sortable: true,
          filterable: true,
          value: "price",
          dataType: "number",
        },

        {
          text: "Total",
          align: "end",
          sortable: true,
          filterable: true,
          value: "total",
          dataType: "number",
        },
      ],
      // Cabeceras actualizadas para incluir la tasa aplicada
      header_invoices: [
        {
          text: "Comprobante",
          align: "start",
          sortable: true,
          filterable: true,
          value: "invoice",
          dataType: "text",
        },

        {
          text: "Subtotal",
          align: "end",
          sortable: true,
          filterable: true,
          value: "subtotal",
          dataType: "number",
        },
        {
          text: "Descuento",
          align: "end",
          sortable: true,
          filterable: true,
          value: "discount",
          dataType: "number",
        },
        {
          text: "Base Gravable",
          align: "end",
          sortable: true,
          filterable: true,
          value: "price",
          dataType: "number",
        },
        {
          text: "Impuesto",
          align: "end",
          sortable: true,
          filterable: true,
          value: "taxes",
          dataType: "number",
        },
        {
          text: "%",
          align: "center",
          sortable: true,
          filterable: true,
          value: "tax_rate",
          dataType: "number",
        },
        {
          text: "Total",
          align: "end",
          sortable: true,
          filterable: true,
          value: "total",
          dataType: "number",
        },
        {
          text: "Imp.Bolsas",
          align: "end",
          sortable: true,
          filterable: true,
          value: "bags",
          dataType: "number",
        },
      ],
      header_payments: [
        {
          text: "Forma de Pago",
          align: "start",
          sortable: true,
          filterable: true,
          value: "type",
          dataType: "text",
        },
        {
          text: "Cantidad",
          align: "end",
          sortable: true,
          filterable: true,
          value: "quantity",
          dataType: "number",
        },
        {
          text: "Valor",
          align: "end",
          sortable: true,
          filterable: true,
          value: "value",
          dataType: "number",
        },
      ],
      docs: [],
    };
  },
  methods: {
    // Método para determinar si se debe mostrar el impuesto de bolsas para esta línea
    shouldShowBags(item) {
      if (!item.invoice) return false;

      // Si ya procesamos esta factura, no mostramos las bolsas
      if (this.processedInvoices.has(item.invoice)) {
        return false;
      }

      // Marcar esta factura como procesada
      this.processedInvoices.add(item.invoice);
      return true;
    },

    // Método para calcular el total de impuestos de bolsas sin duplicación
    getTotalBags() {
      const uniqueInvoices = new Set();
      let totalBags = 0;

      this.invoices.forEach((item) => {
        if (!uniqueInvoices.has(item.invoice) && item.bags) {
          totalBags += parseFloat(item.bags || 0);
          uniqueInvoices.add(item.invoice);
        }
      });

      return totalBags;
    },

    printDiv() {
      const printContents = this.$refs.printArea.innerHTML;
      const originalContents = document.body.innerHTML;

      document.body.innerHTML = printContents;
      window.print();
      document.body.innerHTML = originalContents;
    },
    get_stores() {
      var qry = {};
      qry.store = window.profile.store_id;
      this.loading_status = true;
      webserver("get_pos", qry, (data) => {
        this.loading_status = false;
        if (data) {
          data.forEach((item) => {
            item.codigo = item.store_id;
            item.valor = item.store_name;
          });
        }
        this.stores = data;
      });
    },
    // Método para calcular y mostrar la tasa aplicada
    calculateTaxAmount(price, taxRate) {
      if (!price || !taxRate) return "0.00%";
      const basePrice = parseFloat(price || 0);
      const rate = parseFloat(taxRate || 0);

      // Calcular qué porcentaje representa el impuesto aplicado en relación al precio base
      const taxAmount = ((basePrice * rate) / 100).toFixed(2);
      return this.formatCurrency(taxAmount);
    },
    // Método para obtener totales numéricos
    getTotal(items, key) {
      return items.reduce((sum, item) => sum + parseFloat(item[key] || 0), 0);
    },
    // Método para obtener totales formateados para valores no monetarios (como cantidades)
    getTotalFormatted(items, key) {
      const total = this.getTotal(items, key);
      return total.toLocaleString("es");
    },
    get_docs() {
      const qry = {
        store: window.profile.store_id,
        store_id: window.profile.store_id,
        issueDate: this.issueDate,
        filters: [],
      };

      this.loading_status = true;
      // Resetear el conjunto de facturas procesadas
      this.processedInvoices = new Set();

      console.log(qry);
      webserver("sales_report_fe", qry, (data) => {
        console.log(data);
        this.loading_status = false;

        // Procesar los datos recibidos para asegurar que todos los valores numéricos estén correctamente formateados
        if (data.sales) {
          this.sales = data.sales.map((item) => ({
            ...item,
            quantity: parseFloat(item.quantity || 0),
            taxes: parseFloat(item.taxes || 0),
            price: parseFloat(item.price || 0),
            tax_rate: parseFloat(item.tax_rate || 0),
            // Añadir campo calculado para el total
            total: parseFloat(item.price || 0) + parseFloat(item.taxes || 0),
          }));
        } else {
          this.sales = [];
        }

        if (data.invoices) {
          this.invoices = data.invoices.map((item) => ({
            ...item,
            quantity: parseFloat(item.quantity || 0),
            subtotal: parseFloat(item.subtotal || 0),
            discount: parseFloat(item.discount || 0),
            price: parseFloat(item.price || 0),
            taxes: parseFloat(item.taxes || 0),
            total: parseFloat(item.total || 0),
            bags: parseFloat(item.bags || 0),
            tax_rate: parseFloat(item.tax_rate || 0),
          }));
        } else {
          this.invoices = [];
        }

        if (data.payments) {
          this.payments = data.payments.map((item) => ({
            ...item,
            quantity: parseFloat(item.quantity || 0),
            value: parseFloat(item.value || 0),
          }));
        } else {
          this.payments = [];
        }

        this.resolutions = data.resolutions || [];
        this.seller = data.seller || this.seller;
      });
    },
    formatCurrency(value) {
      // Asegurarse de que value sea un número
      value = parseFloat(value || 0);
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },
    reorderData(dataArray, order) {
      return dataArray.map((data) => {
        let reorderedData = {};
        order.forEach((key) => {
          reorderedData[key] = data[key];
        });
        return reorderedData;
      });
    },
    generatePDF() {
      // Preparar los datos para el PDF con agrupación por tasa de impuesto y cálculo de tasa aplicada
      var categorySales = [...this.sales];
      categorySales.forEach((item) => {
        item.taxes = parseFloat(item.taxes || 0);
        item.price = parseFloat(item.price || 0);
        item.quantity = parseFloat(item.quantity || 0);
        item.tax_rate = parseFloat(item.tax_rate || 0);
        item.tax_amount = ((item.price * item.tax_rate) / 100).toFixed(2);
        item.total = item.price + item.taxes;
      });

      // Agregar fila de totales
      categorySales.push({
        category: "Totales",
        tax_rate: "",
        quantity: this.getTotal(this.sales, "quantity"),
        taxes: this.getTotal(this.sales, "taxes"),
        price: this.getTotal(this.sales, "price"),
        tax_amount: "-",
        total: this.getTotal(this.sales, "price") + this.getTotal(this.sales, "taxes"),
      });

      var pagos = [...this.payments];
      pagos.forEach((item) => {
        item.value = parseFloat(item.value || 0);
        item.quantity = parseFloat(item.quantity || 0);
      });

      pagos.push({
        type: "Totales",
        quantity: this.getTotal(this.payments, "quantity"),
        value: this.getTotal(this.payments, "value"),
      });

      // Preparar las facturas para el PDF con cálculo de tasa aplicada
      // Resetear el conjunto de facturas procesadas para el PDF
      this.processedInvoices = new Set();

      var facturas = [...this.invoices];
      facturas.forEach((item) => {
        item.subtotal = parseFloat(item.subtotal || 0);
        item.discount = parseFloat(item.discount || 0);
        item.price = parseFloat(item.price || 0);
        item.taxes = parseFloat(item.taxes || 0);
        item.total = parseFloat(item.total || 0);
        item.bags = parseFloat(item.bags || 0);
        item.tax_rate = parseFloat(item.tax_rate || 0);
        item.tax_amount = ((item.price * item.tax_rate) / 100).toFixed(2);

        // Si ya procesamos esta factura, eliminar el impuesto de bolsas para evitar duplicación
        if (this.processedInvoices.has(item.invoice)) {
          item.bags = 0;
        } else {
          this.processedInvoices.add(item.invoice);
        }
      });

      var tableHeaders = this.header_invoices.map((header) => header.value);
      var ventas = this.reorderData(facturas, tableHeaders);

      // Agregar fila de totales para facturas
      ventas.push({
        invoice: "Totales",
        tax_rate: "",
        subtotal: this.getTotal(facturas, "subtotal"),
        discount: this.getTotal(facturas, "discount"),
        price: this.getTotal(facturas, "price"),
        taxes: this.getTotal(facturas, "taxes"),
        tax_amount: "-",
        total: this.getTotal(facturas, "total"),
        bags: this.getTotalBags(), // Usar el método para obtener el total correcto de bolsas
      });

      const pdf = new jsPDF("p", "mm", "a4");
      let currentY = 10; // Coordenada Y inicial para la primera tabla

      // Añadir el título del reporte y la fecha de impresión
      const reportTitle = "Comprobante de Informe Diario Facturación Electrónica";
      const printDate = `Fecha de impresión: ${new Date().toLocaleDateString("es-CO")}`;

      pdf.setFontSize(16);
      pdf.text(reportTitle, 10, 10);
      pdf.setFontSize(10);
      pdf.text(printDate, 10, 20);
      currentY = 30; // Coordenada Y inicial para la primera tabla

      const tables = [
        {
          headers: this.header_store || [],
          items: [this.seller] || [],
          title: "Información de la Tienda",
        },
        {
          headers: this.header_resolution || [],
          items: this.resolutions || [],
          title: "Resolución de Facturación Electrónica",
        },
        {
          headers: this.header_sales || [],
          items: categorySales || [],
          title: "Discriminación de Ventas por Departamento",
        },
        {
          headers: this.header_payments || [],
          items: pagos || [],
          title: "Recaudos de Venta",
        },
        {
          headers: this.header_invoices || [],
          items: ventas || [],
          title: "Registros de Facturas por Tasa de Impuesto",
        },
      ];

      tables.forEach((table, index) => {
        if (index > 0) {
          currentY += 10; // Añadir espacio entre tablas
        }

        // Añadir título con estilo personalizado
        pdf.setFontSize(10); // Tamaño de fuente del título
        pdf.text(table.title, 10, currentY);

        const tableData = table.items.map((item) => {
          return Object.values(item).map((value) => {
            if (typeof value === "number") {
              return this.formatCurrency(value);
            } else if (value === null || value === undefined) {
              return "";
            } else {
              return value;
            }
          });
        });

        const tableHeaders = table.headers.map((header) => header.text);

        // Determinar las columnas que son numéricas
        const numericColumns = [];
        if (table.items.length > 0) {
          Object.keys(table.items[0]).forEach((key, colIndex) => {
            if (typeof table.items[0][key] === "number") {
              numericColumns.push(colIndex);
            }
          });
        }

        const columnStyles = {};
        numericColumns.forEach((colIndex) => {
          columnStyles[colIndex] = { halign: "right" };
        });

        pdf.autoTable({
          startY: currentY + 5, // Añadir un espacio antes de empezar la tabla
          head: [tableHeaders],
          body: tableData,
          theme: "grid", // Puedes cambiar el tema si deseas
          styles: { cellPadding: 2, fontSize: 8 },
          headStyles: { fontSize: 8, fillColor: [95, 149, 140] },
          footStyles: { fontSize: 8, fillColor: [44, 238, 144] },
          columnStyles: columnStyles,
          didDrawPage: (data) => {
            currentY = data.cursor.y; // Actualizar la coordenada Y para la próxima tabla
          },
        });
      });

      var fileName = this.store + "-" + this.issueDate + ".pdf";
      pdf.save(fileName);
    },
  },
  mounted() {
    this.get_docs();
  },
};
</script>

<style scoped>
/* Your component-specific styles go here */
</style>
